import { useState, useEffect, useMemo } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Typography from '@mui/material/Typography';

import { useTheme } from '@mui/material/styles';

type FieldValues = Record<string, string>;

type RadioFieldDefinition = {
  name: string;
  label: string;
  type: string;
  options?: string[];
  default?: string;
  disabled?: boolean;
  placeholder?: string;
  description?: string;
};

type RadioInputFieldUiOptions = {
  options: {
    label: string;
    value: string;
    fields?: RadioFieldDefinition[];
  }[];
};

function PasswordField(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  required?: boolean;
}) {
  const [show, setShow] = useState(false);

  return (
    <TextField
      label={props.label}
      type={show ? 'text' : 'password'}
      value={props.value}
      onChange={e => props.onChange(e.target.value)}
      onBlur={props.onBlur}
      disabled={props.disabled}
      required={props.required}
      fullWidth
      size="medium"
      variant="outlined"
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={() => setShow(s => !s)}
              edge="end"
              size="medium"
              disabled={props.disabled}
            >
              {show ? (
                <VisibilityOff fontSize="medium" />
              ) : (
                <Visibility fontSize="medium" />
              )}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
}

function parseAndValidateFutureDate(value: string): {
  valid: boolean;
  message?: string;
} {
  if (!value?.trim()) {
    return { valid: true }; // optional field
  }

  const formatRegex = /^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-[0-9]{4}$/;
  if (!formatRegex.test(value)) {
    return {
      valid: false,
      message: 'Error. Inputted text is an invalid format.',
    };
  }

  const [dayStr, monthStr, yearStr] = value.split('-');
  const day = Number(dayStr);
  const month = Number(monthStr);
  const year = Number(yearStr);

  const parsedDate = new Date(year, month - 1, day);

  const isRealDate =
    parsedDate.getFullYear() === year &&
    parsedDate.getMonth() === month - 1 &&
    parsedDate.getDate() === day;

  if (!isRealDate) {
    return {
      valid: false,
      message: 'Error. Inputted text is an invalid format.',
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  parsedDate.setHours(0, 0, 0, 0);

  if (parsedDate <= today) {
    return {
      valid: false,
      message:
        'Error. Inputted date has already passed. Enter a valid future date.',
    };
  }

  return { valid: true };
}

/**
 * Emit all non-empty fields.
 * For apiKeyExpiresAt:
 * - keep the user's typed value in local state for UI
 * - emit it only if valid so schema submit can still block invalid submission
 *
 * For review page masking:
 * - emit apiKeyMasked / clientIdMasked / clientSecretMasked as "******"
 * - keep actual secret fields available for backend submission
 */
function buildEmittedFormData(
  selectedValue: string,
  values: FieldValues,
): Record<string, any> {
  const emitted: Record<string, any> = { type: selectedValue };

  Object.entries(values).forEach(([key, value]) => {
    if (typeof value === 'string' && value.trim() !== '') {
      emitted[key] = value;
    }
  });

  if (selectedValue === 'API Key') {
    // Remove OAuth fields
    delete emitted.tokenUrl;
    delete emitted.clientId;
    delete emitted.clientSecret;
    delete emitted.oauthScope;

    // Emit apiKeyExpiresAt only if valid
    const dateValue = values.apiKeyExpiresAt ?? '';
    const result = parseAndValidateFutureDate(dateValue);

    if (!result.valid) {
      delete emitted.apiKeyExpiresAt;
    }
  }

  if (selectedValue === 'OAuth Client Credentials') {
    // Remove API key fields
    delete emitted.apiKey;
    delete emitted.apiKeyLocation;
    delete emitted.apiKeyLocationName;
    delete emitted.apiKeyExpiresAt;
  }

  return emitted;
}

function FieldInput(props: {
  field: RadioFieldDefinition;
  value: string;
  onChange: (name: string, value: string) => void;
  onBlur: (name: string) => void;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  description?: string;
}) {
  const {
    field,
    value,
    onChange,
    onBlur,
    disabled,
    required,
    placeholder,
    error,
    helperText,
    description,
  } = props;

  if (field.type === 'select' && field.options) {
    return (
      <FormControl fullWidth size="medium" disabled={disabled}>
        <InputLabel>{field.label}</InputLabel>
        <Select
          label={field.label}
          value={value ?? field.default ?? ''}
          onChange={e => onChange(field.name, e.target.value as string)}
          onBlur={() => onBlur(field.name)}
        >
          {field.options.map(opt => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  if (field.type === 'password') {
    return (
      <PasswordField
        label={field.label}
        value={value || ''}
        onChange={v => onChange(field.name, v)}
        onBlur={() => onBlur(field.name)}
        disabled={disabled}
        required={required}
      />
    );
  }

  return (
    <TextField
      label={field.label}
      type={field.type === 'string' ? 'text' : field.type}
      value={value || ''}
      onChange={e => onChange(field.name, e.target.value)}
      onBlur={() => onBlur(field.name)}
      disabled={disabled}
      required={required}
      error={error}
      helperText={helperText ?? description ?? ''}
      fullWidth
      size="medium"
      variant="outlined"
      placeholder={placeholder}
    />
  );
}

export const RadioInputField = (
  props: FieldExtensionComponentProps<
    Record<string, any>,
    RadioInputFieldUiOptions
  >,
) => {
  const {
    onChange,
    formData,
    uiSchema,
    schema: { title = 'Authorization', description },
    rawErrors,
    disabled,
    readonly,
  } = props;

  const options = useMemo(
    () => uiSchema['ui:options']?.options ?? [],
    [uiSchema],
  );

  const theme = useTheme();
  const backgroundColor = theme.palette.mode === 'dark' ? '#0D0D0D' : '#F4F4F4';
  const isEditMode = disabled || readonly;

  const [selectedValue, setSelectedValue] = useState<string>(
    () => formData?.type ?? options[0]?.value ?? '',
  );

  const [fieldValues, setFieldValues] = useState<FieldValues>(() => {
    const {
      type: _type,
      apiKeyMasked: _apiKeyMasked,
      clientIdMasked: _clientIdMasked,
      clientSecretMasked: _clientSecretMasked,
      ...rest
    } = formData ?? {};
    return rest;
  });

  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>(
    {},
  );

  useEffect(() => {
    const activeOption = options.find(o => o.value === selectedValue);
    if (!activeOption) return;

    setFieldValues(prev => {
      const updated: FieldValues = { ...prev };
      let changed = false;

      activeOption.fields?.forEach(field => {
        if (field.default !== undefined && !updated[field.name]) {
          updated[field.name] = field.default;
          changed = true;
        }
      });

      const emitted = buildEmittedFormData(selectedValue, updated);
      onChange(emitted);

      return changed ? updated : prev;
    });
  }, [selectedValue, options, onChange]);

  const handleRadioChange = (value: string) => {
    if (isEditMode) return;

    const selectedOption = options.find(o => o.value === value);
    const defaults: FieldValues = {};

    selectedOption?.fields?.forEach(field => {
      if (field.default !== undefined) {
        defaults[field.name] = field.default;
      }
    });

    setSelectedValue(value);
    setFieldValues(defaults);
    setTouchedFields({});

    const emitted = buildEmittedFormData(value, defaults);
    onChange(emitted);
  };

  const handleFieldChange = (name: string, value: string) => {
    setFieldValues(prev => {
      const updated = { ...prev, [name]: value };
      const emitted = buildEmittedFormData(selectedValue, updated);
      onChange(emitted);
      return updated;
    });
  };

  const handleFieldBlur = (name: string) => {
    setTouchedFields(prev => ({ ...prev, [name]: true }));
  };

  const activeOption = options.find(o => o.value === selectedValue);
  const hasRootError = rawErrors && rawErrors.length > 0;

  const apiKeyExpiryError =
    selectedValue === 'API Key' &&
    touchedFields.apiKeyExpiresAt &&
    fieldValues.apiKeyExpiresAt?.trim()
      ? parseAndValidateFutureDate(fieldValues.apiKeyExpiresAt).message
      : undefined;

  return (
    <Box sx={{ backgroundColor, padding: '1rem', borderRadius: '0.25rem' }}>
      <FormControl component="fieldset" fullWidth error={hasRootError}>
        <FormLabel component="legend">
          {title && (
            <Typography
              variant="h6"
              sx={{
                marginBottom: 1,
                marginTop: 0,
                color: theme.palette.text.primary,
              }}
            >
              {title}
            </Typography>
          )}

          {description && (
            <Typography
              variant="body2"
              sx={{
                marginBottom: 1,
                marginTop: 0,
                color: theme.palette.text.primary,
              }}
            >
              {description}
            </Typography>
          )}
        </FormLabel>

        {(title || description) && (
          <Divider sx={{ marginBottom: 1, marginTop: 0.5 }} />
        )}

        <RadioGroup
          row
          value={selectedValue}
          onChange={e => handleRadioChange(e.target.value)}
        >
          {options.map(opt => (
            <FormControlLabel
              key={opt.value}
              value={opt.value}
              control={<Radio disabled={isEditMode} />}
              label={opt.label}
            />
          ))}
        </RadioGroup>

        {hasRootError ? (
          <FormHelperText>{rawErrors.join(', ')}</FormHelperText>
        ) : null}
      </FormControl>

      {activeOption?.fields?.length ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {activeOption.fields.map(field => {
            const isApiKeyExpiryField = field.name === 'apiKeyExpiresAt';

            return (
              <FieldInput
                key={field.name}
                field={field}
                value={fieldValues[field.name] ?? ''}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                disabled={field?.disabled || isEditMode}
                required={
                  selectedValue === 'API Key'
                    ? [
                        'apiKeyLocation',
                        'apiKeyLocationName',
                        'apiKey',
                      ].includes(field.name)
                    : ['tokenUrl', 'clientId', 'clientSecret'].includes(
                        field.name,
                      )
                }
                placeholder={field?.placeholder}
                error={isApiKeyExpiryField && Boolean(apiKeyExpiryError)}
                helperText={isApiKeyExpiryField ? apiKeyExpiryError : undefined}
                description={field?.description}
              />
            );
          })}
        </Box>
      ) : null}
    </Box>
  );
};
